import type { InvolvementPeriod } from "@insights/contracts";
import { Card, Combobox, TimeSeriesChart } from "@insights/ui";
import { UsersRound } from "lucide-react";
import { useParams, useSearchParams } from "react-router";
import { Loading, PageHeader } from "../../../shared/components";
import { AppShell } from "../../../shared/layout";
import { formatIsoWeek } from "../helpers/format-iso-week";
import {
  useInvolvement,
  useInvolvementOptions,
  type InvolvementFilters,
} from "../hooks/useInvolvement";
import "../styles/involvement.css";

export function normalizeInvolvementPeriod(
  value: string | null,
): InvolvementPeriod {
  if (value === "90") {
    return 90;
  }
  if (value === "180") {
    return 180;
  }
  return 30;
}

export function involvementFiltersFrom(
  searchParams: URLSearchParams,
): InvolvementFilters {
  return {
    period: normalizeInvolvementPeriod(searchParams.get("period")),
    repository: searchParams.get("repository") ?? "",
    project: searchParams.get("project") ?? "",
  };
}

export function involvementComparisonText(
  people: number,
  previousPeople: number | null,
  changePercent: number | null,
) {
  if (previousPeople === null) {
    return "Not enough collected history for comparison";
  }
  const difference = people - previousPeople;
  if (difference === 0) {
    return `No change from the previous period (${previousPeople})`;
  }
  const direction = difference > 0 ? "more" : "fewer";
  const percentage = changePercent === null
    ? ""
    : ` · ${Math.abs(changePercent).toFixed(0)}%`;
  return `${Math.abs(difference)} ${direction} than the previous period (${previousPeople})${percentage}`;
}

export function InvolvementPage() {
  const { workspaceId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const filters = involvementFiltersFrom(searchParams);
  const options = useInvolvementOptions(workspaceId, filters.repository);
  const involvement = useInvolvement(workspaceId, filters);

  function update(next: Partial<InvolvementFilters>) {
    const updated = { ...filters, ...next };
    const params = new URLSearchParams({ period: String(updated.period) });
    if (updated.repository) {
      params.set("repository", updated.repository);
    }
    if (updated.project) {
      params.set("project", updated.project);
    }
    setSearchParams(params);
  }

  const selectedProject = options.data?.projects.find(
    (project) => project.name.toLowerCase() === filters.project.toLowerCase(),
  );

  return (
    <AppShell>
      <PageHeader
        eyebrow="Pulse"
        title="Involvement"
        description="How many people actively trigger builds for a repository project over time?"
      />

      <div className="involvement-filters" aria-label="Involvement filters">
        <Combobox
          label="Period"
          value={String(filters.period)}
          options={[
            { value: "30", label: "30 days" },
            { value: "90", label: "90 days" },
            { value: "180", label: "180 days" },
          ]}
          onValueChange={(value) => {
            update({ period: Number(value) as InvolvementPeriod });
          }}
        />
        <Combobox
          label="Repository"
          {...(filters.repository ? { value: filters.repository } : {})}
          placeholder="Select repository"
          options={(options.data?.repositories ?? []).map((repository) => ({
            value: repository,
            label: repository,
          }))}
          onValueChange={(repository) => {
            update({ repository, project: "" });
          }}
        />
        <Combobox
          disabled={!filters.repository || options.isLoading}
          label="Project"
          {...(selectedProject ? { value: selectedProject.name } : {})}
          placeholder={filters.repository ? "Select project" : "Select repository first"}
          options={(options.data?.projects ?? []).map((project) => ({
            value: project.name,
            label: project.name,
          }))}
          onValueChange={(project) => {
            update({ project });
          }}
        />
      </div>

      {options.isLoading && <Loading />}
      {options.isError && (
        <Card className="involvement-error" role="alert">
          Unable to load involvement options.
        </Card>
      )}
      {!filters.repository && !options.isLoading && !options.isError && (
        <Card className="involvement-empty">
          Select a repository and project to view involvement.
        </Card>
      )}
      {filters.repository
        && !options.isLoading
        && !options.isError
        && options.data?.projects.length === 0 && (
          <Card className="involvement-empty">
            No normalized Terrabuild projects are available for this repository.
          </Card>
        )}
      {involvement.isLoading && <Loading />}
      {involvement.isError && (
        <Card className="involvement-error" role="alert">
          Unable to load involvement metrics.
        </Card>
      )}

      {involvement.data && (
        <>
          <Card as="section" className="involvement-summary">
            <div className="involvement-summary-heading">
              <span className="involvement-summary-icon" aria-hidden="true">
                <UsersRound />
              </span>
              <div>
                <span>Selected period</span>
                <h2>People involved</h2>
              </div>
            </div>
            <strong className="involvement-total">
              {involvement.data.people.toLocaleString()}
            </strong>
            <p>
              {involvementComparisonText(
                involvement.data.people,
                involvement.data.previousPeople,
                involvement.data.changePercent,
              )}
            </p>
            <TimeSeriesChart
              ariaLabel="Distinct build actors by week"
              data={involvement.data.series.map((point) => ({
                label: formatIsoWeek(point.from),
                value: point.people,
              }))}
              emptyState="No build actors were observed during this period."
              formatValue={(value) => `${Math.round(value)} people`}
            />
          </Card>

          <p className="involvement-coverage">
            {involvement.data.collectionStartedAt
              ? `Build activity collected since ${new Date(involvement.data.collectionStartedAt).toLocaleString()}.`
              : "Collection begins with the next Terrabuild build."}
            {involvement.data.unattributedBuildCount > 0
              ? ` ${involvement.data.unattributedBuildCount} build ${involvement.data.unattributedBuildCount === 1 ? "execution has" : "executions have"} no attributable actor.`
              : ""}
          </p>

          <Card as="section" className="involvement-roster">
            <div className="involvement-roster-heading">
              <div>
                <h2>Contributors</h2>
                <p>Build actors observed for {involvement.data.project.name}.</p>
              </div>
              <span>{involvement.data.contributors.length}</span>
            </div>
            {involvement.data.contributors.length ? (
              <div className="involvement-table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th scope="col">Person</th>
                      <th scope="col">Builds</th>
                      <th scope="col">Active weeks</th>
                      <th scope="col">Last build</th>
                    </tr>
                  </thead>
                  <tbody>
                    {involvement.data.contributors.map((contributor) => (
                      <tr key={contributor.identity}>
                        <th scope="row">{contributor.name}</th>
                        <td>{contributor.buildCount}</td>
                        <td>{contributor.activeWeeks}</td>
                        <td>{new Date(contributor.lastBuildAt).toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="involvement-empty-roster">
                No attributable build actors were observed during this period.
              </p>
            )}
          </Card>
        </>
      )}
    </AppShell>
  );
}
